import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu'
import { Add, ArrowDown2 } from 'iconsax-react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch } from '@/hooks/redux'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import {
  selectSelectedTeam,
  selectSelectedWorkStation,
  selectWorkStations,
} from '@/store/api/Users/Users.selector'
import {
  setSelectedTeam,
  setSelectedWorkStation,
} from '@/store/api/Users/Users.slice'
import type { Team, WorkStation } from '@/store/api/Users/Users.types'

const WorkStationSelector = () => {
  const workStations = useSelector(selectWorkStations)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedWorkStation = useSelector(selectSelectedWorkStation)
  const selectedTeam = useSelector(selectSelectedTeam)
  const { hasPermission } = usePermissions()

  const handleTeamSelect = (team: Team, workStation: WorkStation) => {
    dispatch(setSelectedTeam(team))
    dispatch(setSelectedWorkStation(workStation))
  }

  const handleCreateNewTeam = (workspaceID: string) => {
    navigate(AppRoutes.createTeamFirstStep, { state: workspaceID })
  }

  const handleCreateNewWorkspace = () => {
    navigate(AppRoutes.workspaceCreation)
  }

  return (
    <div className="flex w-full justify-center py-4">
      <DropdownMenu>
        <DropdownMenuTrigger className="w-full min-w-0 px-2">
          <div className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border-none py-1 text-sm font-medium text-neutralColor">
            <span className="min-w-0 flex-1 truncate text-left">
              {selectedWorkStation?.name}, {selectedTeam?.name}
            </span>
            <ArrowDown2 size={16} className="shrink-0" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-2 bg-baseColorHighlight">
          <DropdownMenuGroup>
            {workStations.map((ws, wsIndex) => {
              const isLastWs = wsIndex === workStations.length - 1
              const canCreateWorkspace = hasPermission(
                EUserPermission.HANDLE_WORKSPACES,
                EUserRole.CO_OWNER,
              )
              const canCreateTeam = hasPermission(
                EUserPermission.HANDLE_WORKSPACES,
                EUserRole.WORKSPACE_ADMIN,
              )
              return (
                <div key={`${ws._id}-workstation-item`}>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>{ws.name}</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent className="p-2 bg-baseColorHighlight">
                        {ws.teams.map((team, teamIndex) => {
                          const isLastTeam = teamIndex === ws.teams.length - 1
                          return (
                            <div key={`${team._id}-team-item`}>
                              <DropdownMenuItem
                                key={team._id}
                                onFocus={(e) => {
                                  if (
                                    e.currentTarget.closest(
                                      '[aria-hidden="true"]',
                                    )
                                  ) {
                                    e.preventDefault()
                                  }
                                }}
                                onClick={() => handleTeamSelect(team, ws)}
                              >
                                {team.name}
                              </DropdownMenuItem>
                              {(!isLastTeam || canCreateTeam) && (
                                <DropdownMenuSeparator />
                              )}
                            </div>
                          )
                        })}
                        {canCreateTeam && (
                          <DropdownMenuItem>
                            <div
                              onClick={() => handleCreateNewTeam(ws._id)}
                              className="flex items-center gap-2 justify-between w-full"
                            >
                              Create new Team <Add size={16} />
                            </div>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                  {(!isLastWs || canCreateWorkspace) && (
                    <DropdownMenuSeparator />
                  )}
                </div>
              )
            })}
            {hasPermission(
              EUserPermission.HANDLE_WORKSPACES,
              EUserRole.CO_OWNER,
            ) && (
              <DropdownMenuItem>
                <div
                  onClick={() => handleCreateNewWorkspace()}
                  className="flex items-center gap-2 justify-between w-full"
                >
                  Create new Workspace <Add size={16} />
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default WorkStationSelector
